import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';

import { HTTP_STATUS } from '@/api/ErrorCode.model.ts';
import { CustomerErrorDetailsDtoCodeEnum } from '@/api/generated';
import { QueryClientMetaModel } from '@/config/QueryClientMeta.model.ts';
import { ToastConfig } from '@/config/ToastConfig.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { customerErrorUtil } from '@/views/customers/status/customerError.util.ts';

type Options = {
  /** Also handle a 403 without a code (the permission gate of a decision) with a generic message. */
  handleForbidden?: boolean;
};

type Return = {
  /** Shows the translated message of an error handled here; other errors are left to the global toast. */
  handleError: (error: unknown) => void;
  /** Meta of the mutation, so the global error toast skips what is handled here. */
  meta: Pick<QueryClientMetaModel, 'locallyHandledErrorCodes' | 'locallyHandledHttpStatuses'>;
};

/** Translated messages of the customer approval flow errors (Prospekt, Odrzucony klient, Zatwierdzenie klienta). */
export const useCustomerErrorToast = (
  codes: readonly CustomerErrorDetailsDtoCodeEnum[],
  options?: Options,
): Return => {
  const { t } = useTranslationWithPrefix('views.customers.status.errors');
  const handleForbidden = Boolean(options?.handleForbidden);

  const meta = useMemo(
    () => ({
      locallyHandledErrorCodes: [...codes],
      locallyHandledHttpStatuses: handleForbidden ? [HTTP_STATUS.FORBIDDEN] : undefined,
    }),
    [codes, handleForbidden],
  );

  const handleError = useCallback(
    (error: unknown) => {
      const code = customerErrorUtil.code(error);
      if (code && codes.includes(code)) {
        toast(t(code), ToastConfig.error);
        return;
      }
      if (handleForbidden && customerErrorUtil.httpStatus(error) === HTTP_STATUS.FORBIDDEN) {
        toast(t('forbidden'), ToastConfig.error);
      }
    },
    [codes, handleForbidden, t],
  );

  return { handleError, meta };
};
